import React, { useEffect, useState } from "react";
import { Spin, Button, Divider, Alert, Select } from "antd";
import { faNetworkWired } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import BacnetWizardEditor from "./BacnetWizardEditor";
import {
  serializeMappings,
  deserializeMappings,
  normalizeConfig,
  remapStaleMappings,
} from "../../edgeManagement/adapters/CDO/BACNET/CdoBacnetPage";
// INTERFACES
import { AdapterBacnetInterface, BacnetBankMapping } from "../../../interfaces/Adapter.interface";
// REDUX
import { useLazyGetAdapterQuery, usePutAdapterMutation } from "../../../redux/adapters/adapterAPI";
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate } from "../WizardStepGate";
// WIZARD UTILS
import { WizardStep } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
import { describeError } from "../utils/describeError";
// STORE
import { RootState } from "../../../app/store";
// FLATBUFFERS
import { LN2_MESSAGE_CATEGORIES } from "../../../flatbuffers/types/ln2MessageOptions";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { BLANK_ADAPTER_CDO_LN1 } from "../../../constants/adapter";
import WizardExportButton from "../components/WizardExportButton";

// Protocol types that require a non-empty cdo_input on PUT (server validates).
// Includes both numeric (FlatBuffer-decoded) and string (JSON fallback) forms.
const LN1_FAMILY = new Set<number | string>([
  AdapterProtocol.LN1,
  AdapterProtocol.RVU,
  AdapterProtocol.CGA,
  AdapterProtocol.LOBBYVISION,
  "LN1",
  "RVU",
  "CGA",
  "LOBBYVISION",
]);

export default function StepBacnet() {
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, markStepCompleted, goToStep } = useWizard();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [cdoValue, setCdoValue] = useState<AdapterBacnetInterface[]>([]);
  const [bankMappings, setBankMappings] = useState<BacnetBankMapping[]>([]);
  const [messageFilter, setMessageFilter] = useState<Set<string> | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [adapterName, setAdapterName] = useState("");
  const [siteName, setSiteName] = useState("");
  const [siteId, setSiteId] = useState("");
  const [selectedBankId, setSelectedBankId] = useState<string | undefined>(() =>
    wizardState.bankIds.find((id) => !!wizardState.adapterIdByBank[id]),
  );

  // Derive adapter ID from the selected bank
  const selectedAdapterId: string | undefined = selectedBankId
    ? wizardState.adapterIdByBank[selectedBankId]
    : wizardState.adapterIds[0];

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [triggerGetAdapter, { isLoading: isLoadingAdapter }] = useLazyGetAdapterQuery();
  const [triggerGetAdapterKeys] = useLazyGetAdapterKeysQuery();
  const [triggerPutAdapter] = usePutAdapterMutation();

  // ************************************************ */
  // FUNCTIONS ************************************* */

  /** Build message filter from adapter keys */
  const buildMessageFilter = (keys: any[]): Set<string> => {
    const filter = new Set<string>();
    keys.forEach((k: any) => {
      const mc: number = k.messageContent ?? k.message_content;
      if (
        mc < MessageContent.Status_StatusMessage ||
        mc > MessageContent.ControllerInfo_ControllerInfoMessage
      )
        return;
      const category = LN2_MESSAGE_CATEGORIES[mc - 1];
      if (!category) return;
      filter.add(`${category.categoryKey}:${k.messageValue ?? k.message_value}`);
    });
    return filter;
  };

  const loadAdapter = async (adapterId: string) => {
    setLoaded(false);
    const token = await GETJwtToken();
    const result = await triggerGetAdapter({ id: adapterId, token });
    const adapterData = result.data?.data || result.data;
    if (!adapterData) return;

    setAdapterName(adapterData.name ?? "");

    // Fetch adapter keys separately (not included in adapter GET response)
    const keysResult = await triggerGetAdapterKeys({ adapterId, token });
    const keysRaw: unknown = (keysResult.data as any)?.data ?? keysResult.data ?? [];
    setMessageFilter(buildMessageFilter(Array.isArray(keysRaw) ? keysRaw : []));

    // Load existing BACnet config if present.
    // FlatBuffer responses use camelCase (bacnetBankMappingsJson); JSON fallback uses snake_case.
    let parsedMappings: BacnetBankMapping[] = [];
    const rawMappingsField = adapterData.bacnetBankMappingsJson ?? adapterData.bacnet_bank_mappings;
    if (rawMappingsField) {
      try {
        const raw =
          typeof rawMappingsField === "string" ? JSON.parse(rawMappingsField) : rawMappingsField;
        if (Array.isArray(raw)) {
          const deserialized = deserializeMappings(raw);
          // Remap any stale ln2_bank_id / ln2_lift_id values that belong to a
          // previous org (e.g. after deleting a draft and re-importing). The
          // remapped mappings have current-org UUIDs so all downstream hydration
          // and scope resolution works without any name-based fallbacks.
          const { remapped, bankIdMap } = remapStaleMappings(
            deserialized,
            wizardState.bankIds,
            wizardState.bankNames,
            wizardState.liftIdsByBank,
            wizardState.liftNamesByBank,
          );
          parsedMappings = remapped;

          const configField = adapterData.bacnetConfigJson ?? adapterData.bacnet_config;
          if (configField) {
            try {
              const configRaw =
                typeof configField === "string" ? JSON.parse(configField) : configField;
              if (Array.isArray(configRaw)) {
                // Rewrite stale ent.bank_id values using the bank ID map before
                // normalizing, so normalizeConfig sees current UUIDs.
                const rewritten = configRaw.map((item: any) => ({
                  ...item,
                  entries: (item.entries || []).map((ent: any) => ({
                    ...ent,
                    bank_id:
                      typeof ent.bank_id === "string" && bankIdMap[ent.bank_id]
                        ? bankIdMap[ent.bank_id]
                        : ent.bank_id,
                  })),
                }));
                setCdoValue(normalizeConfig(rewritten, parsedMappings));
              }
            } catch {
              setStepError(
                "Failed to parse existing BACnet configuration — the saved data may be malformed.",
              );
            }
          } else {
            setCdoValue([]);
          }
        }
      } catch {
        setStepError(
          "Failed to parse existing BACnet bank mappings — the saved data may be malformed.",
        );
      }
    } else if (adapterData.bacnetConfigJson ?? adapterData.bacnet_config) {
      try {
        const configOnly = adapterData.bacnetConfigJson ?? adapterData.bacnet_config;
        const raw = typeof configOnly === "string" ? JSON.parse(configOnly) : configOnly;
        if (Array.isArray(raw)) {
          setCdoValue(normalizeConfig(raw, parsedMappings));
        }
      } catch {
        setStepError(
          "Failed to parse existing BACnet configuration — the saved data may be malformed.",
        );
      }
    } else {
      setCdoValue([]);
    }
    setBankMappings(parsedMappings);

    setLoaded(true);
  };

  const handleSave = async () => {
    if (!selectedAdapterId) return;
    setSaving(true);
    setStepError(null);
    const token = await GETJwtToken();
    try {
      // Fetch full adapter to preserve existing fields on PUT
      const fullResult = await triggerGetAdapter({
        id: selectedAdapterId,
        token,
      });
      const a = fullResult.data?.data || fullResult.data || {};

      // Only include fields that UpdateAdapterRequest accepts.
      // Keys must be camelCase to match the FlatBuffer request serializer.
      // Read from adapter using both camelCase (FlatBuffer) and snake_case
      // (JSON fallback) for compatibility.
      await triggerPutAdapter({
        id: selectedAdapterId,
        body: {
          serverId: a.serverId ?? a.server_id,
          floors: a.floors,
          liftType: a.liftType ?? a.lift_type,
          version: a.version,
          type: a.type,
          authType: a.authType ?? a.auth_type,
          protocolType: a.protocolType ?? a.protocol_type,
          leadingZeroesId: a.leadingZeroesId ?? a.leading_zeroes_id,
          name: a.name,
          // nullable fields: pass the string directly (serializer does String(v), never parse).
          // jsonField fields: pass string or object — serializer handles both without double-encoding.
          // Never wrap with JSON.stringify: if the adapter already stored a JSON string, wrapping
          // it again produces a double-encoded value; if absent, JSON.stringify({}) → "{}" which
          // the server rejects as "empty".
          dictionaryJson: a.dictionaryJson ?? a.dictionary ?? null,
          interfaceJson: a.interfaceJson ?? a.interface_json ?? null,
          cdoInputJson:
            a.cdoInputJson ||
            (LN1_FAMILY.has(a.protocolType ?? a.protocol_type)
              ? JSON.stringify(BLANK_ADAPTER_CDO_LN1)
              : null),
          offsetMappingJson: a.offsetMappingJson ?? a.offset_mapping ?? null,
          intraContentFlagsJson: a.intraContentFlagsJson ?? a.intra_content_flags ?? null,
          socketMessagePriorityJson:
            a.socketMessagePriorityJson ?? a.socket_message_priority ?? null,
          bacnetConfig: cdoValue,
          bacnetBankMappings: serializeMappings(bankMappings),
        },
        token,
      }).unwrap();
      markStepCompleted(WizardStep.Bacnet);
    } catch (err: any) {
      setStepError(`Failed to save BACnet configuration — ${describeError(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    markStepCompleted(WizardStep.Bacnet);
    goToStep(WizardStep.Users);
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */

  // Auto-mark this step complete whenever the operator is on it.
  // BACnet bank-mapping config is optional — many sites never use it
  // — and Next must work regardless of whether the operator opened
  // the editor. Saving real BACnet config still flows through
  // handleSave and persists to the adapter, but the *step completion
  // gate* shouldn't block an operator who is happy with no BACnet at
  // all.
  //
  // Depending on `currentStep` (not just on mount) re-fires this if
  // an upstream step's unmarkStepCompleted later unmarks this step
  // and the operator returns: by the time the indicator is visible,
  // the step is complete again.
  useEffect(() => {
    if (wizardState.currentStep === WizardStep.Bacnet) {
      markStepCompleted(WizardStep.Bacnet);
    }
  }, [wizardState.currentStep, markStepCompleted]);

  // Left untouched: no useWizardStepRefresh here, deliberately. cdoValue,
  // bankMappings, siteName and siteId are unsaved operator work until "Save
  // BACnet Config" runs, and they are also the wizard draft's own payload
  // (see the useWizardStepDraft call below, gated on `validate: () =>
  // loaded`) — so a refresh-triggered reload would overwrite not just React
  // state but the *persisted* draft too. loadAdapter ends with
  // `setLoaded(true)`, which re-arms that gate, so the very next debounced
  // autosave would write server values over the operator's saved draft with
  // no way to recover by reloading — the exact loss
  // book/src/wizard/wizard-bacnet.md promises does not happen ("leaving the
  // step does not lose them").
  //
  // The gap this leaves is real, not a shrugged-off sliver: the invalidation
  // that matters most in practice (a bank's adapterId changing) is already
  // covered below, because selectedAdapterId is derived from selectedBankId.
  // But ADAPTER_FIELD_RULES.protocolType — "the most catastrophic change —
  // the entire message vocabulary swaps" per its own comment in
  // utils/wizardInvalidation.ts — can change on the SAME adapter id without
  // selectedAdapterId ever changing, and nothing here reacts to that: the
  // operator can keep editing, and saving, BACnet mappings for an adapter
  // that is no longer BACnet. book/src/wizard/wizard-bacnet.md now names
  // this gap for operators; the code fix is still open.
  //
  // TODO(no ticket filed yet): a real fix here is an explicit,
  // operator-consented reload affordance naming the consequence, not a
  // silent refetch — as a follow-up, not folded into this change. This
  // repo's convention is to cite a real ticket for a deferred fix (see
  // e.g. StepNotifications.tsx's "LIFT-276 follow-up"); no ticket exists
  // for this one yet — file one and replace this TODO with its key.
  //
  // Load adapter whenever the resolved adapter ID changes
  useEffect(() => {
    if (selectedAdapterId) {
      loadAdapter(selectedAdapterId);
    }
  }, [selectedAdapterId]);

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // The operator builds BACnet bank/lift mappings and a (potentially
  // large) cdo config in this step. None of that is saved server-side
  // until they click "Save BACnet Config" — until then it's React-local
  // state that a reload would erase. Persist the in-progress draft so a
  // browser crash or navigation away doesn't cost an afternoon's work.
  //
  // selectedBankId is restored only when it points at a bank still in
  // the wizard, dodging the stale-id case when a draft outlives its bank.
  // siteName/siteId are free-text and restored as strings.
  // bankMappings and cdoValue are object arrays; they round-trip through
  // JSON.stringify cleanly because BacnetWizardEditor mutates them via
  // plain React state setters (no class instances, no functions).
  useWizardStepDraft<{
    selectedBankId: string | undefined;
    siteName: string;
    siteId: string;
    bankMappings: BacnetBankMapping[];
    cdoValue: AdapterBacnetInterface[];
  }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Bacnet,
    scopeId: "bacnet",
    scopeType: "step",
    draft: { selectedBankId, siteName, siteId, bankMappings, cdoValue },
    onRestore: (saved) => {
      if (
        typeof saved.selectedBankId === "string" &&
        wizardState.bankIds.includes(saved.selectedBankId)
      ) {
        setSelectedBankId(saved.selectedBankId);
      }
      if (typeof saved.siteName === "string") setSiteName(saved.siteName);
      if (typeof saved.siteId === "string") setSiteId(saved.siteId);
      if (Array.isArray(saved.bankMappings)) setBankMappings(saved.bankMappings);
      if (Array.isArray(saved.cdoValue)) setCdoValue(saved.cdoValue);
    },
    // Skip the save until the adapter has finished loading. Otherwise an
    // intermediate empty state (cdoValue=[]) immediately after a bank
    // change would overwrite a valid persisted draft.
    validate: () => loaded,
  });

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingAdapter || saving}>
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        <DrawerRowDivider icon={faNetworkWired} title="BACnet Configuration" />

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Configure BACnet bank/lift mappings and point descriptions for your adapters. Skip this step if BACnet is not needed."
        />

        {/* BANK SELECTOR */}
        <div className="row mb-3">
          <div className="col-12 col-md-6">
            <span className="generalStyles__inputLabel">Bank</span>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={selectedBankId}
              onChange={(val: string) => setSelectedBankId(val)}
              placeholder="Select a bank"
            >
              {wizardState.bankIds
                .filter((id) => !!wizardState.adapterIdByBank[id])
                .map((id) => (
                  <Select.Option key={id} value={id}>
                    {wizardState.bankNames[id] || id}
                  </Select.Option>
                ))}
            </Select>
          </div>
          {selectedAdapterId && (
            <div className="col-12 col-md-6" style={{ display: "flex", alignItems: "flex-end" }}>
              <span style={{ color: "#888" }}>Adapter: {adapterName || selectedAdapterId}</span>
            </div>
          )}
        </div>

        {/* BACNET WIZARD EDITOR */}
        {selectedAdapterId && loaded && (
          <div style={{ marginTop: 16 }}>
            <BacnetWizardEditor
              bankIds={wizardState.bankIds}
              bankNames={wizardState.bankNames}
              liftNamesByBank={wizardState.liftNamesByBank}
              liftIdsByBank={wizardState.liftIdsByBank}
              messageFilter={messageFilter}
              cdoValue={cdoValue}
              setCdo={setCdoValue}
              bankMappings={bankMappings}
              setBankMappings={setBankMappings}
              siteName={siteName}
              setSiteName={setSiteName}
              siteId={siteId}
              setSiteId={setSiteId}
            />
          </div>
        )}

        {/* SAVE / SKIP BUTTONS */}
        <Divider />
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
        >
          <div>
            {/* Backend stage 7 = BACnet. Previously hardcoded as 6
                (pre-ChannelModifiers numbering); the wrong scope was
                downloaded for every export after the step shift. */}
            <WizardExportButton stage={7} orgId={wizardState.organizationId} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleSkip} size="large">
              Skip BACnet
            </Button>
            <WizardOpGate op="update">
              <Button
                type="primary"
                onClick={handleSave}
                disabled={!selectedAdapterId}
                loading={saving}
                size="large"
              >
                Save BACnet Config
              </Button>
            </WizardOpGate>
          </div>
        </div>
        {stepError && (
          <Alert
            type="error"
            showIcon
            message="BACnet configuration error"
            description={stepError}
            style={{ marginTop: 16 }}
            closable
            onClose={() => setStepError(null)}
          />
        )}
      </div>
    </Spin>
  );
}
